/**
 * Education Component
 * Displays educational background in a timeline format
 *  CORRIGÉ : Numérotation (04) et gestion des données
 */
import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import * as styles from './education.css';
import { commonClasses } from "../../../../styles/indexStyles";
import { useLanguage } from "../../../../hooks";
import { useTheme } from "../../../shared/context/indexSharedContext";
import { apiService } from '../../../shared/services/apiService';
import { normalizeImageUrl } from '../../../shared/utils/imageUrl';
import { Spinner } from '../../../admin/components/Spinner/Spinner';

// ================================
// INTERFACES
// ================================

interface EducationItem {
  id: string;
  title_fr: string;
  title_en: string;
  institution_fr: string;
  institution_en: string;
  year: string;
  start_date?: string;
  end_date?: string;
  level_fr?: string;
  level_en?: string;
  description_fr?: string;
  description_en?: string;
  rncp_code?: string;
  certification_url?: string;
  field_fr?: string;
  field_en?: string;
  icon_type: string;
  color: string;
  image_url?: string;
  order_index: number;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

const hasProperty = (obj: any, prop: string): boolean => obj && prop in obj;

// ================================
// COMPOSANT PRINCIPAL
// ================================

export const Education: React.FC = () => {
  // ================================
  // HOOKS
  // ================================
  
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  
  // ================================
  // ÉTAT LOCAL
  // ================================
  
  const [educationData, setEducationData] = useState<EducationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [animate, setAnimate] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [retryCount, setRetryCount] = useState(0);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docUrl, setDocUrl] = useState<string | null>(null);

  // ================================
  // UTILITAIRES POUR ADAPTATION THÈME
  // ================================
  const getThemeColors = () => {
    const isLight = theme === 'light';
    return {
      cardBg: isLight ? '#f8f9fa' : '#112240',  // Utilise #112240 en mode sombre et #f8f9fa en mode light
      cardBorder: isLight ? '#d1d5db' : 'rgba(100, 255, 218, 0.1)',
      text: isLight ? '#1a1a1a' : '#ccd6f6',
      textSecondary: isLight ? '#6b7280' : '#8892b0',
      primary: isLight ? '#0066cc' : '#64ffda',
      bgAccent: isLight ? 'rgba(0, 102, 204, 0.08)' : 'rgba(100, 255, 218, 0.05)',
      borderAccent: isLight ? 'rgba(0, 102, 204, 0.2)' : 'rgba(100, 255, 218, 0.1)',
    };
  };

  // ================================
  // CHARGEMENT DES DONNÉES
  // ================================
  
  // ✅ CORRECTION - Transformer en useCallback pour éviter les boucles infinies
  const loadEducationData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // ✅ PROTECTION ANTI-BOUCLE - Utilise apiService avec cache 3 minutes
      const response = await apiService.get<EducationItem[]>('/education', true, {
        cacheTtlMs: 180000, // 3 minutes cache
        useCache: true
      });
      
      if (response?.data) {
  //  ASSERTION DE TYPE pour éviter 'never'
  const data = response.data as any;
  let educationArray: any[] = [];
  
  //  PARSING DES DONNÉES AVEC TYPE GUARDS
  if (Array.isArray(data)) {
    educationArray = data;
  } else if (data && typeof data === 'object') {
    // Vérifications avec guards
    if (hasProperty(data, 'education') && Array.isArray(data.education)) {
      educationArray = data.education;
    } else if (hasProperty(data, 'data') && Array.isArray(data.data)) {
      educationArray = data.data;
    } else if (hasProperty(data, 'items') && Array.isArray(data.items)) {
      educationArray = data.items;
    } else if (hasProperty(data, 'id')) {
      educationArray = [data];
    } else {
      educationArray = [];
    }
  }
        
        
        if (educationArray.length > 0) {
          //  MAPPER et valider les données
          const validatedEducation: EducationItem[] = educationArray.map((item: any, index: number) => ({
            id: item.id || `edu-${index}`,
            title_fr: item.title_fr || item.degree_fr || item.title || 'Formation',
            title_en: item.title_en || item.degree_en || item.title || 'Education',
            institution_fr: item.institution_fr || item.institution || 'Institution',
            institution_en: item.institution_en || item.institution || 'Institution',
            year: item.year || item.start_date?.substring(0, 4) || new Date().getFullYear().toString(),
            start_date: item.start_date,
            end_date: item.end_date,
            level_fr: item.level_fr || item.level,
            level_en: item.level_en || item.level,
            description_fr: item.description_fr || item.description,
            description_en: item.description_en || item.description,
            rncp_code: item.rncp_code,
            certification_url: item.certification_url,
            field_fr: item.field_fr || item.field,
            field_en: item.field_en || item.field,
            icon_type: item.icon_type || 'graduation',
            color: item.color || '#3498db',
            image_url: item.image_url || '',
            order_index: item.order_index ?? index,
            is_visible: item.is_visible !== false,
            created_at: item.created_at || new Date().toISOString(),
            updated_at: item.updated_at || new Date().toISOString()
          }));
          
          //  FILTRER et TRIER
          const visibleEducation = validatedEducation
            .filter(item => item.is_visible)
            .sort((a, b) => a.order_index - b.order_index);
          
          setEducationData(visibleEducation);
          setRetryCount(0);
        } else {
          setEducationData([]);
        }
      } else {
        setEducationData([]);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur de chargement');
      setEducationData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ CORRECTION - useEffect séparé avec dépendance [language]
  useEffect(() => {
    loadEducationData();
  }, [loadEducationData]);

  // ================================
  // ANIMATION AU SCROLL
  // ================================
  
  useEffect(() => {
    if (inView && !animate) {
      const timer = setTimeout(() => setAnimate(true), 200);
      return () => clearTimeout(timer);
    }
  }, [inView, animate]);

  // ================================
  // UTILITAIRES
  // ================================
  
  const toggleExpanded = (id: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getEducationIcon = (iconType: string): string => {
    const iconMap: Record<string, string> = {
      graduation: '🎓',
      certificate: '📜',
      university: '🏫',
      dataScience: '📊',
      webDev: '💻',
      medical: '🏥',
      business: '💼',
      engineering: '⚙️',
      design: '🎨',
      language: '🌍',
      default: '🎓'
    };
    return iconMap[iconType] || iconMap.default;
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getDisplayTitle = (item: EducationItem): string => {
    // FR: afficher la valeur FR avec fallback neutre
    if (language === 'fr') return item.title_fr || item.title_en || 'Formation';
    // EN: afficher UNIQUEMENT la valeur EN (pas de fallback FR)
    return item.title_en || 'Education';
  };

  const getDisplayInstitution = (item: EducationItem): string => {
    if (language === 'fr') return item.institution_fr || item.institution_en || 'Institution';
    // EN-only
    return item.institution_en || 'Institution';
  };

  const getDisplayDescription = (item: EducationItem): string | undefined => {
    if (language === 'fr') return item.description_fr || item.description_en || undefined;
    // EN-only
    return item.description_en || undefined;
  };

  const getDisplayLevel = (item: EducationItem): string | undefined => {
    if (language === 'fr') return item.level_fr || item.level_en || undefined;
    // EN-only
    return item.level_en || undefined;
  };

  const retryLoad = () => {
    setRetryCount(prev => prev + 1);
    loadEducationData();
  };

  // ================================
  // RENDU CONDITIONNEL - CHARGEMENT
  // ================================
  
  if (loading) {
    return (
      <section id="education" className={`${commonClasses.section} ${styles.section}`}>
        <div className={styles.container}>
          <h2 className={`${commonClasses.title} ${styles.title}`}>
            <span className={`${commonClasses.highlight} ${styles.highlight}`}>04.</span>
            {t('education.title') || 'Formation'}
          </h2>
          
          <div className={styles.loading}>
            <Spinner size="large" ariaLabel="Chargement des formations" />
          </div>
        </div>
      </section>
    );
  }

  // ================================
  // RENDU CONDITIONNEL - ERREUR
  // ================================
  
  if (error && educationData.length === 0) {
    return (
      <section id="education" className={`${commonClasses.section} ${styles.section}`}>
        <div className={styles.container}>
          <h2 className={`${commonClasses.title} ${styles.title}`}>
            <span className={`${commonClasses.highlight} ${styles.highlight}`}>04.</span>
            {t('education.title') || 'Formation'}
          </h2>
          
          <div className={styles.error}>
            <h3>❌ Erreur de chargement</h3>
            <p>{error}</p>
            <p>Tentatives : {retryCount}/2</p>
            <button 
              onClick={retryLoad}
              disabled={retryCount >= 2}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: retryCount >= 2 ? '#666' : '#64ffda',
                color: retryCount >= 2 ? '#ccc' : '#0a192f',
                border: 'none',
                borderRadius: '4px',
                cursor: retryCount >= 2 ? 'not-allowed' : 'pointer'
              }}
            >
              {retryCount >= 2 ? 'Limite atteinte' : (t('common.retry') || 'Réessayer')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ================================
  // RENDU CONDITIONNEL - AUCUNE DONNÉE
  // ================================
  
  if (educationData.length === 0) {
    const colors = getThemeColors();
    return (
      <section id="education" className={`${commonClasses.section} ${styles.section}`}>
        <div className={styles.container}>
          <h2 className={`${commonClasses.title} ${styles.title}`}>
            <span className={`${commonClasses.highlight} ${styles.highlight}`}>04.</span>
            {t('education.title') || 'Formation'}
          </h2>
          
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: colors.textSecondary,
            backgroundColor: colors.bgAccent,
            borderRadius: '8px',
            border: `1px solid ${colors.borderAccent}`
          }}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: colors.text }}>
              🎓 Formations à venir
            </h3>
            <p style={{ fontSize: '1rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              Les informations sur ma formation seront bientôt disponibles.
            </p>
            <button 
              onClick={retryLoad}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: colors.primary,
                color: theme === 'light' ? '#ffffff' : '#0a192f',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 500
              }}
            >
              {t('common.retry') || 'Réessayer'}
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ================================
  // RENDU PRINCIPAL
  // ================================
  
  return (
    <section
      id="education"
      className={`${commonClasses.section} ${styles.section}`}
      ref={ref}
      aria-labelledby="education-heading"
    >
      <div className={styles.container}>
        
        {/* ✅ TITRE AVEC NUMÉROTATION CORRIGÉE */}
        <h2 
          id="education-heading"
          className={`${commonClasses.title} ${styles.title}`}
        >
          <span className={`${commonClasses.highlight} ${styles.highlight}`}>04.</span>
          <span data-translate="true">
            {t('education.title') || 'Formation'}
          </span>
        </h2>

         {/* ✅ TIMELINE DES FORMATIONS */}
        <div className={styles.timeline}>
          
          <ul className={styles.educationList} role="list">
            {educationData.map((education, index) => {
              const isExpanded = expandedItems.has(education.id);
              const description = getDisplayDescription(education);
              const level = getDisplayLevel(education);
              
              return (
                <li 
                  key={education.id}
                  className={`${styles.timelineItem} ${isExpanded ? styles.timelineItemExpanded : ''}`}
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    opacity: animate ? 1 : 0,
                    transform: animate ? 'translateY(0)' : 'translateY(20px)',
                    transition: `opacity 0.6s ease-out ${index * 0.1}s, transform 0.6s ease-out ${index * 0.1}s`
                  }}
                >
                  {/* Timeline dot */}
                  <div className={styles.timelineDot}></div>

                  <div className={styles.timelineContent}>
                    <div className={`${styles.educationCard} ${isExpanded ? styles.expanded : ''}`}
                    >
                  {/* Header de la carte */}
                  <div className={styles.headerContainer}>
                    <div className={styles.textContainer}>
                      <div 
                        className={styles.cardHeader}
                        onClick={() => toggleExpanded(education.id)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        aria-controls={`education-details-${education.id}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleExpanded(education.id);
                          }
                        }}
                      >
                        <div 
                          className={styles.iconContainer}
                          style={{ backgroundColor: education.color }}
                          aria-hidden="true"
                        >
                          {education.image_url ? (
                            <img
                              src={normalizeImageUrl(education.image_url)}
                              alt=""
                              style={{ width: '60%', height: '60%', objectFit: 'contain' }}
                            />
                          ) : (
                            getEducationIcon(education.icon_type)
                          )}
                        </div>
                        
                        <div className={styles.headerContent}>
                          <h3 className={styles.cardTitle}>
                            {getDisplayTitle(education)}
                          </h3>
                          
                          <div className={styles.cardMeta}>
                            <span className={styles.institution}>
                              <span className={styles.metaIcon}>🏫</span>
                              {getDisplayInstitution(education)}
                            </span>
                            
                            <span className={styles.year}>
                              <span className={styles.metaIcon}>📅</span>
                              {education.start_date && education.end_date ? 
                                `${formatDate(education.start_date)} - ${formatDate(education.end_date)}` :
                                education.year
                              }
                            </span>
                          </div>
                        </div>
                        
                        <button 
                          className={styles.expandButton}
                          aria-label={isExpanded ? 'Réduire' : 'Développer'}
                        >
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Contenu détaillé (développable) */}
                  {isExpanded && (
                    <div 
                      id={`education-details-${education.id}`}
                      className={styles.cardBody}
                    >
                      {description && (
                        <div className={styles.description}>
                          {description}
                        </div>
                      )}
                      
                      <div className={styles.additionalInfo}>
                        {level && (
                          <div className={styles.level}>
                            <strong>Niveau :</strong> {level}
                          </div>
                        )}
                        
                        {education.rncp_code && (
                          <div className={styles.rncp}>
                            <strong>Code RNCP :</strong> {education.rncp_code}
                          </div>
                        )}
                        
                        {education.certification_url && (
                          <div className={styles.certificationLink}>
                            <button
                              type="button"
                              className={styles.viewCertButton}
                              onClick={() => window.open(education.certification_url!, '_blank', 'noopener,noreferrer')}
                            >
                              <span>📜</span>
                              Voir le document
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* ✅ INDICATEUR DE FALLBACK si erreur mais données chargées */}
        {error && educationData.length > 0 && (
          <div className={styles.fallbackIndicator}>
            <span>⚠️ Certaines données peuvent être incomplètes - Erreur : {error}</span>
          </div>
        )}
      </div>
      {docModalOpen && docUrl && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Document de certification"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setDocModalOpen(false)}
        >
          <div
            style={{ width: '90vw', height: '85vh', background: '#0a192f', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(100,255,218,0.2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'rgba(100,255,218,0.08)', borderBottom: '1px solid rgba(100,255,218,0.2)' }}>
              <span style={{ color: '#64ffda', fontWeight: 600 }}>Document</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <a href={docUrl} target="_blank" rel="noreferrer" style={{ color: '#64ffda', textDecoration: 'underline', fontSize: 12 }}>Ouvrir dans un nouvel onglet</a>
                <button onClick={() => setDocModalOpen(false)} style={{ background: 'transparent', color: '#ccd6f6', border: '1px solid rgba(204,214,246,0.3)', padding: '4px 8px', borderRadius: 4 }}>Fermer</button>
              </div>
            </div>
            <iframe
              title="document"
              src={docUrl}
              style={{ width: '100%', height: 'calc(85vh - 40px)', border: 0, background: '#fff' }}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Education;